import { api } from '@gik/core/api';
import Order, { OrderCreationStatus } from '@gik/core/models/gik/Order';
import sleep from '@gik/core/utils/sleep';
import { ResponsePromise } from 'ky';
import { CheckoutFormPayload } from '../components/CheckoutForm/CheckoutForm';
import { PaymentConfirmationValues } from '../types';
import { StatusCodes } from 'http-status-codes';
import i18n from '@gik/i18n';
import noop from '@gik/core/utils/noop';
import { AffiliateTracking } from '@gik/core/utils';

const PATH = 'orders';

interface CreateOrderResponse {
  orderId: number;
  orderKey: string; // exists to stop bots from being able to crawl orders
}

export async function placeOrder(
  payload: CheckoutFormPayload,
  updateCheckoutStatusText: (text: string) => void = noop
): Promise<PaymentConfirmationValues> {
  if (payload.createClaimRequest && payload.messageToRecipient) {
    // copy the secure message to recipient into the claim so it can be viewed by the supporter
    payload.createClaimRequest.note = payload.messageToRecipient;
  }

  updateCheckoutStatusText('PLACING ORDER');
  const createOrderRequest: ResponsePromise = api.post(PATH, { method: 'POST', json: payload });
  const createOrderResult = (await createOrderRequest).status;
  if (createOrderResult !== StatusCodes.OK) {
    throw new Error(i18n.t('checkout.genericBackendError'));
  }
  const createOrderResponse = await createOrderRequest.json<CreateOrderResponse>();

  updateCheckoutStatusText('PROCESSING PAYMENT');
  const { orderId, orderKey } = createOrderResponse;

  const maxAttempts = 30;
  const pollInterval = 500;
  for (let i = 1; ; i++) {
    if (i > maxAttempts) {
      console.error(`Max attempts reached for ${orderId}`);
      throw new Error(`Order timed out. Please contact support with reference #${orderId}`);
    }
    await sleep(pollInterval);
    console.log(`Polling order #${orderId} - ${i}`);

    let order: Order = null;
    try {
      const pollOrderRequest: ResponsePromise = api.get(
        `${PATH}/${orderId}?orderKey=${orderKey}&timezoneOffset=${payload.timezoneOffset}`,
        {
          timeout: 10000,
          retry: 0,
        }
      );
      order = await pollOrderRequest.json<Order>();

      updateCheckoutStatusText(order.creationStatusMessage);
    } catch (error) {
      // if a poll fails we will retry, so just log the error
      console.error(error);
      continue;
    }

    switch (order.creationStatus) {
      case OrderCreationStatus.Pending:
        break;
      case OrderCreationStatus.Success:
        if (payload.inkindRouteId) {
          try {
            await AffiliateTracking.trackConversion(order.id.toString(), payload.inkindRouteId, 'ordertip', order.tip);
          } catch (error) {
            console.error(
              'Failed to track affiliate conversion for purchase for inkind page',
              payload.inkindRouteId,
              error
            );
          }
        }
        return {
          productName: payload.products[0].name,
          orderDate: order.orderDate,
          orderId,
          orderKey,
          tip: order.tip,
          shipping: order.shipping,
          products: order.products,
          senderEmail: order.senderEmail,
          paymentMethod: order.paymentMethod,
          subtotal: order.subtotal,
          total: order.total,
          shippingCost: order.shippingCost,
          taxCost: order.taxCost,
          claimStatus: order.claimStatus,
        };
      case OrderCreationStatus.Fail:
        throw new Error(order.errorMessage);
      default:
        throw new Error('Unknown order creation status: ' + order.creationStatus);
    }
  }
}
